import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  Briefcase, 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  DollarSign,
  Tag
} from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Modal from '../../components/UI/Modal';
import Card from '../../components/UI/Card';
import Table from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useSettings } from '../../contexts/SettingsContext';

interface Job {
  _id: string;
  name: string;
  description?: string;
  price: number;
  sku?: string;
  model?: string;
  brand?: any;
  schedule?: any;
  section?: any;
  type?: any;
  unit?: any;
}

const JobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  
  // Dropdown data
  const [schedules, setSchedules] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [compressorTypes, setCompressorTypes] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  
  const [formData, setFormData] = useState({
    name: '',
    schedule: '',
    section: '',
    parentCategory: '',
    subCategory: '',
    brand: '',
    model: '',
    sku: '',
    type: '',
    compressorType: '',
    countryOfOrigin: '',
    countryOfAssembly: '',
    details: '',
    description: '',
    specifications: '',
    coolingCapacity: '',
    serialNo: '',
    partNo: '',
    price: '',
    unit: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const { settings } = useSettings();

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchJobs(),
        fetchSchedules(),
        fetchSections(),
        fetchCategories(),
        fetchBrands(),
        fetchTypes(),
        fetchCompressorTypes(),
        fetchCountries(),
        fetchUnits(),
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const fetchJobs = async () => {
    try {
      const response = await apiService.getJobs();
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await apiService.getSchedules();
      setSchedules(response.data.schedules || response.data || []);
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchSections = async () => {
    try {
      const response = await apiService.getSections();
      setSections(response.data.sections || response.data || []);
    } catch (error) {
      console.error('Error fetching sections:', error);
      setSections([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiService.getCategories();
      setCategories(response.data.categories || response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await apiService.getBrands();
      setBrands(response.data.brands || response.data || []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      setBrands([]);
    }
  };

  const fetchTypes = async () => {
    try {
      const response = await apiService.getTypes();
      setTypes(response.data.types || response.data || []);
    } catch (error) {
      console.error('Error fetching types:', error);
      setTypes([]);
    }
  };

  const fetchCompressorTypes = async () => {
    try {
      const response = await apiService.getCompressorTypes();
      setCompressorTypes(response.data.compressorTypes || response.data || []);
    } catch (error) {
      console.error('Error fetching compressor types:', error);
      setCompressorTypes([]);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiService.getCountries();
      setCountries(response.data.countries || response.data || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setCountries([]);
    }
  };

  const fetchUnits = async () => {
    try {
      const response = await apiService.getUnits();
      setUnits(response.data.units || response.data || []);
    } catch (error) {
      console.error('Error fetching units:', error);
      setUnits([]);
    }
  };

  const handleOpenModal = (job?: Job) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        name: job.name || '',
        schedule: (job as any).schedule?._id || (job as any).schedule || '',
        section: (job as any).section?._id || (job as any).section || '',
        parentCategory: (job as any).parentCategory?._id || (job as any).parentCategory || '',
        subCategory: (job as any).subCategory?._id || (job as any).subCategory || '',
        brand: (job as any).brand?._id || (job as any).brand || '',
        model: (job as any).model || '',
        sku: job.sku || '',
        type: (job as any).type?._id || (job as any).type || '',
        compressorType: (job as any).compressorType?._id || (job as any).compressorType || '',
        countryOfOrigin: (job as any).countryOfOrigin?._id || (job as any).countryOfOrigin || '',
        countryOfAssembly: (job as any).countryOfAssembly?._id || (job as any).countryOfAssembly || '',
        details: (job as any).details || '',
        description: job.description || '',
        specifications: (job as any).specifications || '',
        coolingCapacity: (job as any).coolingCapacity || '',
        serialNo: (job as any).serialNo || '',
        partNo: (job as any).partNo || '',
        price: job.price?.toString() || '',
        unit: (job as any).unit?._id || (job as any).unit || '',
      });
    } else {
      setEditingJob(null);
      setFormData({
        name: '',
        schedule: '',
        section: '',
        parentCategory: '',
        subCategory: '',
        brand: '',
        model: '',
        sku: '',
        type: '',
        compressorType: '',
        countryOfOrigin: '',
        countryOfAssembly: '',
        details: '',
        description: '',
        specifications: '',
        coolingCapacity: '',
        serialNo: '',
        partNo: '',
        price: '',
        unit: '',
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingJob(null);
    setFormData({
      name: '',
      schedule: '',
      section: '',
      parentCategory: '',
      subCategory: '',
      brand: '',
      model: '',
      sku: '',
      type: '',
      compressorType: '',
      countryOfOrigin: '',
      countryOfAssembly: '',
      details: '',
      description: '',
      specifications: '',
      coolingCapacity: '',
      serialNo: '',
      partNo: '',
      price: '',
      unit: '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Job name is required');
      return;
    }

    if (!formData.price || isNaN(parseFloat(formData.price))) {
      toast.error('Valid price is required');
      return;
    }

    try {
      setSubmitting(true);
      const payload: any = {
        name: formData.name,
        price: parseFloat(formData.price),
      };

      // Add optional fields only if they have values
      if (formData.schedule) payload.schedule = formData.schedule;
      if (formData.section) payload.section = formData.section;
      if (formData.parentCategory) payload.parentCategory = formData.parentCategory;
      if (formData.subCategory) payload.subCategory = formData.subCategory;
      if (formData.brand) payload.brand = formData.brand;
      if (formData.model) payload.model = formData.model;
      if (formData.sku) payload.sku = formData.sku;
      if (formData.type) payload.type = formData.type;
      if (formData.compressorType) payload.compressorType = formData.compressorType;
      if (formData.countryOfOrigin) payload.countryOfOrigin = formData.countryOfOrigin;
      if (formData.countryOfAssembly) payload.countryOfAssembly = formData.countryOfAssembly;
      if (formData.details) payload.details = formData.details;
      if (formData.description) payload.description = formData.description;
      if (formData.specifications) payload.specifications = formData.specifications;
      if (formData.coolingCapacity) payload.coolingCapacity = formData.coolingCapacity;
      if (formData.serialNo) payload.serialNo = formData.serialNo;
      if (formData.partNo) payload.partNo = formData.partNo;
      if (formData.unit) payload.unit = formData.unit;

      if (editingJob) {
        await apiService.updateJob(editingJob._id, payload);
        toast.success('Job updated successfully');
      } else {
        await apiService.createJob(payload);
        toast.success('Job created successfully');
      }
      handleCloseModal();
      fetchJobs();
    } catch (error: any) {
      console.error('Error saving job:', error);
      toast.error(error.response?.data?.message || 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this job?')) {
      return;
    }

    try {
      await apiService.deleteJob(id);
      toast.success('Job deleted successfully');
      fetchJobs();
    } catch (error: any) {
      console.error('Error deleting job:', error);
      toast.error(error.response?.data?.message || 'Failed to delete job');
    }
  };

  const filteredJobs = jobs.filter(job =>
    job.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.model?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      header: 'Job',
      accessor: (row: Job) => (
        <div>
          <div className="text-sm font-medium text-gray-900">{row.name}</div>
          {row.description && (
            <div className="text-sm text-gray-500 truncate max-w-xs">{row.description}</div>
          )}
        </div>
      ),
    },
    {
      header: 'SKU',
      accessor: (row: Job) => (
        row.sku ? (
          <div className="flex items-center text-sm text-gray-900">
            <Tag className="w-4 h-4 mr-2 text-gray-400" />
            {row.sku}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
    {
      header: 'Brand',
      accessor: (row: Job) => (
        row.brand?.name ? (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {row.brand.name}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )
      ),
    },
    {
      header: 'Model',
      accessor: (row: Job) => (
        <span className="text-sm text-gray-900">{row.model || '-'}</span>
      ),
    },
    {
      header: 'Price',
      accessor: (row: Job) => (
        <div className="flex items-center text-sm font-semibold text-gray-900">
          <DollarSign className="w-4 h-4 mr-1 text-green-600" />
          {settings?.currencySymbol || '$'}{row.price.toLocaleString()}
        </div>
      ),
    },
    {
      header: 'Actions',
      accessor: (row: Job) => (
        <div className="flex gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
            icon={<Edit2 className="w-4 h-4" />}
            onClick={() => handleOpenModal(row)}
          />
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(row._id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
            Jobs
          </h1>
          <p className="mt-2 text-sm text-gray-600" style={{ fontFamily: 'var(--font-family)' }}>
            Manage jobs and services for quotations
          </p>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Button
            icon={<Plus className="w-4 h-4" />}
            onClick={() => handleOpenModal()}
          >
            Add Job
          </Button>
        </motion.div>
      </motion.div>

      {/* Search Bar */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card padding="md">
          <Input
            placeholder="Search jobs by name, description, SKU, or model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={<Search className="w-5 h-5" />}
            fullWidth
          />
        </Card>
      </motion.div>

      {/* Jobs Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card padding="none">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredJobs.length > 0 ? (
            <Table
              data={filteredJobs}
              columns={columns}
              emptyMessage="No jobs found"
            />
          ) : (
            <div className="px-6 py-12 text-center">
              <Briefcase className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                No jobs yet
              </h3>
              <p className="mt-1 text-sm text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
                Get started by adding your first job or service.
              </p>
              <div className="mt-6">
                <Button
                  icon={<Plus className="w-4 h-4" />}
                  onClick={() => handleOpenModal()}
                >
                  Add Job
                </Button>
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Add/Edit Job Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={editingJob ? 'Edit Job' : 'Add New Job'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          {/* Job Name */}
          <Input
            label="Job Name *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter job name"
            required
            fullWidth
          />

          {/* Schedule & Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule</label>
              <select
                value={formData.schedule}
                onChange={(e) => setFormData({ ...formData, schedule: e.target.value })}
                className="form-select"
              >
                <option value="">Select Schedule</option>
                {Array.isArray(schedules) && schedules.map((schedule) => (
                  <option key={schedule._id} value={schedule._id}>
                    {schedule.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
              <select
                value={formData.section}
                onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                className="form-select"
              >
                <option value="">Select Section</option>
                {Array.isArray(sections) && sections.map((section) => (
                  <option key={section._id} value={section._id}>
                    {section.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Categories */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Parent Category</label>
              <select
                value={formData.parentCategory}
                onChange={(e) => setFormData({ ...formData, parentCategory: e.target.value })}
                className="form-select"
              >
                <option value="">Select Parent Category</option>
                {Array.isArray(categories) && categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sub Category</label>
              <select
                value={formData.subCategory}
                onChange={(e) => setFormData({ ...formData, subCategory: e.target.value })}
                className="form-select"
              >
                <option value="">Select Sub Category</option>
                {Array.isArray(categories) && categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Brand & Model */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
              <select
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                className="form-select"
              >
                <option value="">Select Brand</option>
                {Array.isArray(brands) && brands.map((brand) => (
                  <option key={brand._id} value={brand._id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Model"
              value={formData.model}
              onChange={(e) => setFormData({ ...formData, model: e.target.value })}
              placeholder="Enter model"
              fullWidth
            />
          </div>

          {/* SKU & Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="SKU"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              placeholder="Enter SKU code"
              fullWidth
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="form-select"
              >
                <option value="">Select Type</option>
                {Array.isArray(types) && types.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Compressor Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Compressor Type</label>
            <select
              value={formData.compressorType}
              onChange={(e) => setFormData({ ...formData, compressorType: e.target.value })}
              className="form-select"
            >
              <option value="">Select Compressor Type</option>
              {Array.isArray(compressorTypes) && compressorTypes.map((ct) => (
                <option key={ct._id} value={ct._id}>
                  {ct.name}
                </option>
              ))}
            </select>
          </div>

          {/* Countries */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country of Origin</label>
              <select
                value={formData.countryOfOrigin}
                onChange={(e) => setFormData({ ...formData, countryOfOrigin: e.target.value })}
                className="form-select"
              >
                <option value="">Select Country</option>
                {Array.isArray(countries) && countries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country of Assembly</label>
              <select
                value={formData.countryOfAssembly}
                onChange={(e) => setFormData({ ...formData, countryOfAssembly: e.target.value })}
                className="form-select"
              >
                <option value="">Select Country</option>
                {Array.isArray(countries) && countries.map((country) => (
                  <option key={country._id} value={country._id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Details */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={formData.details}
              onChange={(e) => setFormData({ ...formData, details: e.target.value })}
              placeholder="Enter job details"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter job description"
            />
          </div>

          {/* Specifications */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Specifications</label>
            <textarea
              className="form-textarea"
              rows={3}
              value={formData.specifications}
              onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
              placeholder="Enter job specifications"
            />
          </div>

          {/* Cooling Capacity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cooling Capacity</label>
            <Input
              value={formData.coolingCapacity}
              onChange={(e) => setFormData({ ...formData, coolingCapacity: e.target.value })}
              placeholder="e.g., 12000 BTU, 1.5 Ton"
              fullWidth
            />
          </div>

          {/* Serial No & Part No */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Serial No."
              value={formData.serialNo}
              onChange={(e) => setFormData({ ...formData, serialNo: e.target.value })}
              placeholder="Enter serial number"
              fullWidth
            />

            <Input
              label="Part No."
              value={formData.partNo}
              onChange={(e) => setFormData({ ...formData, partNo: e.target.value })}
              placeholder="Enter part number"
              fullWidth
            />
          </div>

          {/* Price & Unit */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Price *"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="0.00"
              required
              fullWidth
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
              <select
                value={formData.unit}
                onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                className="form-select"
              >
                <option value="">Select Unit</option>
                {Array.isArray(units) && units.map((unit) => (
                  <option key={unit._id} value={unit._id}>
                    {unit.name} ({unit.symbol})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t sticky bottom-0 bg-white">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
            >
              {editingJob ? 'Update Job' : 'Create Job'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default JobsPage;

